import {
  getAllArticleCategories,
  deleteArticleCategory,
  editArticleCategory,
  createArticleCategory,
} from 'http/articleCategoryApi';

import { IArticleCategory, ICreateArticleCategory } from 'common/types';
import { notify } from 'common/utils';
import { computed, makeAutoObservable, observable } from 'mobx';
import { ArticleCategoryModel } from 'store/article-categoriy-model';

export class ArticleCategoriesViewModel {
  _articleCategories: ArticleCategoryModel[] = [];
  _loading = false;

  get loading() {
    return this._loading;
  }

  set loading(value: boolean) {
    this._loading = value;
  }

  get articleCategories() {
    return this._articleCategories;
  }

  set articleCategories(articleCategories: ArticleCategoryModel[]) {
    this.articleCategories = articleCategories;
  }

  get skeleton() {
    return new Array(5).fill(new ArticleCategoryModel());
  }

  removeArticleCategory = async (id: number) => {
    const updatedArticleCategories = this._articleCategories.filter(
      (articleCaterory) => articleCaterory.id !== id,
    );

    const result = await deleteArticleCategory(id);

    if (result) {
      this._articleCategories = updatedArticleCategories;

      notify(
        <div>
          Article category <strong>{result.name}</strong> was removed
          succesfully!
        </div>,
      );
    }
  };

  manageArticleCategory = async (
    id: number | null = null,
    values: ICreateArticleCategory,
  ) => {
    this.loading = true;

    const articleToUpdate = this.articleCategories.find(
      (articleCaterory) => articleCaterory.id === id,
    );

    try {
      if (articleToUpdate) {
        const updatedArticleCategory = await editArticleCategory(
          articleToUpdate.id,
          values,
        );

        if (updatedArticleCategory) {
          this._articleCategories = this.articleCategories.map(
            (articleCategory) => {
              if (articleCategory.id === updatedArticleCategory.id) {
                return new ArticleCategoryModel(
                  articleCategory.id,
                  updatedArticleCategory,
                );
              }

              return articleCategory;
            },
          );
        }
      } else {
        const newArticleCategory = await createArticleCategory(values);
        if (newArticleCategory) {
          this._articleCategories = [
            ...this.articleCategories,
            new ArticleCategoryModel(newArticleCategory.id, newArticleCategory),
          ];
        }
      }

      notify(
        <div>
          Article category <strong>{values.name}</strong> was{' '}
          {articleToUpdate ? 'updated' : 'created'} succesfully!
        </div>,
      );

      return true;
    } catch (e) {
      notify(
        `Failed to ${articleToUpdate ? 'update' : 'create'} article category`,
        'error',
      );
    } finally {
      this.loading = false;
    }
  };

  fetchArticleCategories = async () => {
    this.loading = true;

    try {
      const articleCategories = await getAllArticleCategories();

      this._articleCategories = articleCategories.map(
        (articleCategory: IArticleCategory) =>
          new ArticleCategoryModel(articleCategory.id, articleCategory),
      );

      return articleCategories;
    } catch (e) {
      notify(`Error, reason:${e}`, 'error');
    } finally {
      this.loading = false;
    }
  };

  constructor() {
    this.fetchArticleCategories();

    makeAutoObservable(this, {
      _loading: observable,
      _articleCategories: observable,

      loading: computed,
    });
  }
}
